/* tslint:disable */
/* eslint-disable */
/**
 * Scrivio Security API
 * Scrivio API for email verification, MFA management and ending an impersonated session.  ## Authentication All endpoints require Firebase Authentication JWT token in `Authorization: Bearer <token>` header.  ## Error Responses All error responses follow RFC 7807 Problem Details format. 
 *
 * The version of the OpenAPI document: 1.0.0
 * Contact: support@scrivio.nl
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import type { Configuration } from './configuration';
import type { AxiosPromise, AxiosInstance, RawAxiosRequestConfig } from 'axios';
import globalAxios from 'axios';
// Some imports not used depending on template conditions
// @ts-ignore
import { DUMMY_BASE_URL, assertParamExists, setApiKeyToObject, setBasicAuthToObject, setBearerAuthToObject, setOAuthToObject, setSearchParams, serializeDataIfNeeded, toPathString, createRequestFunction } from './common';
import type { RequestArgs } from './base';
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, BaseAPI, RequiredError, operationServerMap } from './base';

export interface ImpersonationSessionResponse {
    /**
     * The admin acting as the target
     */
    'adminUserId': string;
    /**
     * That admin\'s e-mailadres
     */
    'adminEmail'?: string | null;
    /**
     * The user the session acts as
     */
    'targetUserId': string;
    /**
     * The target\'s e-mailadres
     */
    'targetEmail': string;
    /**
     * Whether the target\'s e-mailadres is verified, as for their own sign-in
     */
    'targetEmailVerified': boolean;
    /**
     * When the session started; null when the session\'s start cannot be read, in which case it is refused as expired
     */
    'startedAt'?: string | null;
    /**
     * When the session stops authenticating, one hour after it started
     */
    'expiresAt'?: string | null;
}
export interface MfaEnrollmentRequest {
    /**
     * MFA method that was enrolled
     */
    'method': MfaEnrollmentRequestMethodEnum;
}

export const MfaEnrollmentRequestMethodEnum = {
    Totp: 'totp',
    Sms: 'sms'
} as const;

export type MfaEnrollmentRequestMethodEnum = typeof MfaEnrollmentRequestMethodEnum[keyof typeof MfaEnrollmentRequestMethodEnum];

export interface MfaPolicyResponse {
    /**
     * Whether MFA is required for all users
     */
    'required': boolean;
    /**
     * Number of days users have to enable MFA
     */
    'gracePeriodDays': number;
    /**
     * Timestamp when MFA requirement was enabled (milliseconds)
     */
    'requiredSince'?: number | null;
    /**
     * Timestamp when grace period ends (milliseconds)
     */
    'gracePeriodEndsAt'?: number | null;
}
export interface MfaStatsResponse {
    /**
     * Total number of active users
     */
    'totalUsers': number;
    /**
     * Number of users with MFA enabled
     */
    'usersWithMfa': number;
    /**
     * Number of users without MFA
     */
    'usersWithoutMfa': number;
    /**
     * Number of users with verified email
     */
    'usersWithVerifiedEmail': number;
    /**
     * Percentage of users with MFA enabled
     */
    'mfaAdoptionPercent': number;
}
export interface MfaStatusResponse {
    /**
     * Whether MFA is enabled for the user
     */
    'enabled': boolean;
    /**
     * Preferred MFA method (totp or sms)
     */
    'preferredMethod'?: MfaStatusResponsePreferredMethodEnum | null;
    /**
     * Timestamp when MFA was enrolled (milliseconds)
     */
    'enrolledAt'?: number | null;
    /**
     * Number of unused recovery codes remaining
     */
    'recoveryCodesRemaining': number;
    /**
     * Whether the user can enroll in MFA (requires verified email)
     */
    'canEnroll': boolean;
    /**
     * Whether MFA is required by the organization
     */
    'mfaRequired'?: boolean;
    /**
     * Whether the MFA grace period has expired
     */
    'gracePeriodExpired'?: boolean;
}

export const MfaStatusResponsePreferredMethodEnum = {
    Totp: 'totp',
    Sms: 'sms'
} as const;

export type MfaStatusResponsePreferredMethodEnum = typeof MfaStatusResponsePreferredMethodEnum[keyof typeof MfaStatusResponsePreferredMethodEnum];

export interface ProblemDetail {
    /**
     * A URI reference that identifies the problem type
     */
    'type': string;
    /**
     * A short, human-readable summary of the problem
     */
    'title': string;
    /**
     * The HTTP status code
     */
    'status': number;
    /**
     * A human-readable explanation specific to this occurrence
     */
    'detail'?: string;
    /**
     * A URI reference that identifies the specific occurrence
     */
    'instance'?: string;
    /**
     * List of validation errors
     */
    'errors'?: Array<ProblemDetailErrorsInner>;
}
export interface ProblemDetailErrorsInner {
    /**
     * Field name that failed validation
     */
    'field'?: string;
    /**
     * Error message
     */
    'message'?: string;
}
export interface RecoveryCodesResponse {
    /**
     * List of recovery codes. Each code is 8 characters formatted as XXXX-XXXX. **Save these securely - they are shown only once!** 
     */
    'codes': Array<string>;
}
export interface UpdateMfaPolicyRequest {
    /**
     * Whether to require MFA for all users
     */
    'required': boolean;
    /**
     * Number of days users have to enable MFA
     */
    'gracePeriodDays'?: number;
}
export interface UserMfaStatus {
    /**
     * User ID
     */
    'userId': string;
    /**
     * User email
     */
    'email': string;
    /**
     * User\'s full name
     */
    'fullName': string;
    /**
     * Whether email is verified
     */
    'emailVerified': boolean;
    /**
     * Whether MFA is enabled
     */
    'mfaEnabled': boolean;
    /**
     * Whether the MFA grace period has expired for this user
     */
    'gracePeriodExpired': boolean;
}
export interface UsersWithoutMfaResponse {
    'users': Array<UserMfaStatus>;
}
export interface VerifyRecoveryCode200Response {
    'valid'?: boolean;
    'remainingCodes'?: number;
}
export interface VerifyRecoveryCodeRequest {
    /**
     * Recovery code (case-insensitive, dashes optional)
     */
    'code': string;
}

/**
 * OrganizationMfaApi - axios parameter creator
 */
export const OrganizationMfaApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Returns the MFA policy settings for the user\'s organization
         * @summary Get organization MFA policy
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getOrganizationMfaSettings: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/organizations/me/mfa/settings`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns MFA adoption statistics for the organization
         * @summary Get organization MFA statistics
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getOrganizationMfaStats: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/organizations/me/mfa/stats`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns a list of active users who haven\'t enabled MFA
         * @summary Get users without MFA
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getUsersWithoutMfa: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/organizations/me/mfa/users-without-mfa`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Updates the MFA policy for the organization. Requires security.manage permission. 
         * @summary Update organization MFA policy
         * @param {UpdateMfaPolicyRequest} updateMfaPolicyRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateOrganizationMfaSettings: async (updateMfaPolicyRequest: UpdateMfaPolicyRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'updateMfaPolicyRequest' is not null or undefined
            assertParamExists('updateOrganizationMfaSettings', 'updateMfaPolicyRequest', updateMfaPolicyRequest)
            const localVarPath = `/api/v1/organizations/me/mfa/settings`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateMfaPolicyRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * OrganizationMfaApi - functional programming interface
 */
export const OrganizationMfaApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = OrganizationMfaApiAxiosParamCreator(configuration)
    return {
        /**
         * Returns the MFA policy settings for the user\'s organization
         * @summary Get organization MFA policy
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getOrganizationMfaSettings(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<MfaPolicyResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getOrganizationMfaSettings(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['OrganizationMfaApi.getOrganizationMfaSettings']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns MFA adoption statistics for the organization
         * @summary Get organization MFA statistics
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getOrganizationMfaStats(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<MfaStatsResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getOrganizationMfaStats(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['OrganizationMfaApi.getOrganizationMfaStats']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns a list of active users who haven\'t enabled MFA
         * @summary Get users without MFA
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getUsersWithoutMfa(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<UsersWithoutMfaResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getUsersWithoutMfa(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['OrganizationMfaApi.getUsersWithoutMfa']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Updates the MFA policy for the organization. Requires security.manage permission. 
         * @summary Update organization MFA policy
         * @param {UpdateMfaPolicyRequest} updateMfaPolicyRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateOrganizationMfaSettings(updateMfaPolicyRequest: UpdateMfaPolicyRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<MfaPolicyResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateOrganizationMfaSettings(updateMfaPolicyRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['OrganizationMfaApi.updateOrganizationMfaSettings']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * OrganizationMfaApi - factory interface
 */
export const OrganizationMfaApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = OrganizationMfaApiFp(configuration)
    return {
        /**
         * Returns the MFA policy settings for the user\'s organization
         * @summary Get organization MFA policy
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getOrganizationMfaSettings(options?: RawAxiosRequestConfig): AxiosPromise<MfaPolicyResponse> {
            return localVarFp.getOrganizationMfaSettings(options).then((request) => request(axios, basePath));
        },
        /**
         * Returns MFA adoption statistics for the organization
         * @summary Get organization MFA statistics
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getOrganizationMfaStats(options?: RawAxiosRequestConfig): AxiosPromise<MfaStatsResponse> {
            return localVarFp.getOrganizationMfaStats(options).then((request) => request(axios, basePath));
        },
        /**
         * Returns a list of active users who haven\'t enabled MFA
         * @summary Get users without MFA
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getUsersWithoutMfa(options?: RawAxiosRequestConfig): AxiosPromise<UsersWithoutMfaResponse> {
            return localVarFp.getUsersWithoutMfa(options).then((request) => request(axios, basePath));
        },
        /**
         * Updates the MFA policy for the organization. Requires security.manage permission. 
         * @summary Update organization MFA policy
         * @param {UpdateMfaPolicyRequest} updateMfaPolicyRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateOrganizationMfaSettings(updateMfaPolicyRequest: UpdateMfaPolicyRequest, options?: RawAxiosRequestConfig): AxiosPromise<MfaPolicyResponse> {
            return localVarFp.updateOrganizationMfaSettings(updateMfaPolicyRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * OrganizationMfaApi - object-oriented interface
 */
export class OrganizationMfaApi extends BaseAPI {
    /**
     * Returns the MFA policy settings for the user\'s organization
     * @summary Get organization MFA policy
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getOrganizationMfaSettings(options?: RawAxiosRequestConfig) {
        return OrganizationMfaApiFp(this.configuration).getOrganizationMfaSettings(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns MFA adoption statistics for the organization
     * @summary Get organization MFA statistics
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getOrganizationMfaStats(options?: RawAxiosRequestConfig) {
        return OrganizationMfaApiFp(this.configuration).getOrganizationMfaStats(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns a list of active users who haven\'t enabled MFA
     * @summary Get users without MFA
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getUsersWithoutMfa(options?: RawAxiosRequestConfig) {
        return OrganizationMfaApiFp(this.configuration).getUsersWithoutMfa(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Updates the MFA policy for the organization. Requires security.manage permission. 
     * @summary Update organization MFA policy
     * @param {UpdateMfaPolicyRequest} updateMfaPolicyRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateOrganizationMfaSettings(updateMfaPolicyRequest: UpdateMfaPolicyRequest, options?: RawAxiosRequestConfig) {
        return OrganizationMfaApiFp(this.configuration).updateOrganizationMfaSettings(updateMfaPolicyRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * SecurityApi - axios parameter creator
 */
export const SecurityApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Records that an admin ended the impersonated session the caller is in, as an `IMPERSONATION_ENDED` entry in the admin audit log beside the session\'s `IMPERSONATION_STARTED` (#4680).  It is called by the **impersonated** session and not from the admin portal: the tab ending an impersonation holds the impersonated user\'s token, not the admin\'s. There is no request body. The admin, the impersonated user, the organisation and the session\'s start are all read from the caller\'s verified token, so the endpoint cannot write an entry about anyone else.  Calling it again for the same session records nothing further.  Ending is the client signing out. For a Firebase session this endpoint revokes nothing: the refresh tokens belong to the impersonated user\'s Firebase account, so revoking them would also sign the real user out of their own sessions. A backend-issued session (an admin signed in through Keycloak) is ended here too, so its token stops authenticating. 
         * @summary End the impersonation this session is
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        endImpersonation: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/impersonation/end`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Generates a new set of 10 recovery codes for the user. Any existing unused recovery codes are deleted.  **Important:** Recovery codes are shown only once. The user must save them securely. 
         * @summary Generate new recovery codes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        generateRecoveryCodes: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/security/mfa/recovery-codes`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Who is acting as whom in the caller\'s impersonated session, and until when — what the app shows in its impersonation banner and counts down to.  A Firebase session also carries this in its token\'s claims. A session an admin signed in through Keycloak started is an opaque token issued by the backend, which the app cannot read, so this is where the app learns it (ADR-045). Everything is read from the caller\'s verified session. 
         * @summary The impersonation this session is
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getImpersonationSession: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/impersonation/session`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Returns the current MFA status for the authenticated user
         * @summary Get MFA status
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMfaStatus: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/security/mfa/status`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Records that the user has disabled MFA. Call this after removing all MFA factors in Firebase. This also deletes all recovery codes. 
         * @summary Record MFA disabled
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        recordMfaDisabled: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/security/mfa/disabled`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Records that the user has enrolled in MFA. Call this after successfully completing MFA enrollment in Firebase. 
         * @summary Record MFA enrollment
         * @param {MfaEnrollmentRequest} mfaEnrollmentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        recordMfaEnrollment: async (mfaEnrollmentRequest: MfaEnrollmentRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'mfaEnrollmentRequest' is not null or undefined
            assertParamExists('recordMfaEnrollment', 'mfaEnrollmentRequest', mfaEnrollmentRequest)
            const localVarPath = `/api/v1/security/mfa/enrolled`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(mfaEnrollmentRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Synchronizes the user\'s MFA status from Firebase to the database. Call this after enrolling or unenrolling MFA factors in Firebase. 
         * @summary Sync MFA status from Firebase
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        syncMfaStatus: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/security/mfa/sync`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Verifies and consumes a recovery code. Used for account recovery when the user cannot use their normal MFA method. 
         * @summary Verify a recovery code
         * @param {VerifyRecoveryCodeRequest} verifyRecoveryCodeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        verifyRecoveryCode: async (verifyRecoveryCodeRequest: VerifyRecoveryCodeRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'verifyRecoveryCodeRequest' is not null or undefined
            assertParamExists('verifyRecoveryCode', 'verifyRecoveryCodeRequest', verifyRecoveryCodeRequest)
            const localVarPath = `/api/v1/security/mfa/verify-recovery-code`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(verifyRecoveryCodeRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * SecurityApi - functional programming interface
 */
export const SecurityApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = SecurityApiAxiosParamCreator(configuration)
    return {
        /**
         * Records that an admin ended the impersonated session the caller is in, as an `IMPERSONATION_ENDED` entry in the admin audit log beside the session\'s `IMPERSONATION_STARTED` (#4680).  It is called by the **impersonated** session and not from the admin portal: the tab ending an impersonation holds the impersonated user\'s token, not the admin\'s. There is no request body. The admin, the impersonated user, the organisation and the session\'s start are all read from the caller\'s verified token, so the endpoint cannot write an entry about anyone else.  Calling it again for the same session records nothing further.  Ending is the client signing out. For a Firebase session this endpoint revokes nothing: the refresh tokens belong to the impersonated user\'s Firebase account, so revoking them would also sign the real user out of their own sessions. A backend-issued session (an admin signed in through Keycloak) is ended here too, so its token stops authenticating. 
         * @summary End the impersonation this session is
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async endImpersonation(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.endImpersonation(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SecurityApi.endImpersonation']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Generates a new set of 10 recovery codes for the user. Any existing unused recovery codes are deleted.  **Important:** Recovery codes are shown only once. The user must save them securely. 
         * @summary Generate new recovery codes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async generateRecoveryCodes(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<RecoveryCodesResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.generateRecoveryCodes(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SecurityApi.generateRecoveryCodes']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Who is acting as whom in the caller\'s impersonated session, and until when — what the app shows in its impersonation banner and counts down to.  A Firebase session also carries this in its token\'s claims. A session an admin signed in through Keycloak started is an opaque token issued by the backend, which the app cannot read, so this is where the app learns it (ADR-045). Everything is read from the caller\'s verified session. 
         * @summary The impersonation this session is
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getImpersonationSession(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ImpersonationSessionResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getImpersonationSession(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SecurityApi.getImpersonationSession']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Returns the current MFA status for the authenticated user
         * @summary Get MFA status
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getMfaStatus(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<MfaStatusResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getMfaStatus(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SecurityApi.getMfaStatus']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Records that the user has disabled MFA. Call this after removing all MFA factors in Firebase. This also deletes all recovery codes. 
         * @summary Record MFA disabled
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async recordMfaDisabled(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<MfaStatusResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.recordMfaDisabled(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SecurityApi.recordMfaDisabled']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Records that the user has enrolled in MFA. Call this after successfully completing MFA enrollment in Firebase. 
         * @summary Record MFA enrollment
         * @param {MfaEnrollmentRequest} mfaEnrollmentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async recordMfaEnrollment(mfaEnrollmentRequest: MfaEnrollmentRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<MfaStatusResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.recordMfaEnrollment(mfaEnrollmentRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SecurityApi.recordMfaEnrollment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Synchronizes the user\'s MFA status from Firebase to the database. Call this after enrolling or unenrolling MFA factors in Firebase. 
         * @summary Sync MFA status from Firebase
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async syncMfaStatus(options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<MfaStatusResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.syncMfaStatus(options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SecurityApi.syncMfaStatus']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Verifies and consumes a recovery code. Used for account recovery when the user cannot use their normal MFA method. 
         * @summary Verify a recovery code
         * @param {VerifyRecoveryCodeRequest} verifyRecoveryCodeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async verifyRecoveryCode(verifyRecoveryCodeRequest: VerifyRecoveryCodeRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<VerifyRecoveryCode200Response>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.verifyRecoveryCode(verifyRecoveryCodeRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['SecurityApi.verifyRecoveryCode']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * SecurityApi - factory interface
 */
export const SecurityApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = SecurityApiFp(configuration)
    return {
        /**
         * Records that an admin ended the impersonated session the caller is in, as an `IMPERSONATION_ENDED` entry in the admin audit log beside the session\'s `IMPERSONATION_STARTED` (#4680).  It is called by the **impersonated** session and not from the admin portal: the tab ending an impersonation holds the impersonated user\'s token, not the admin\'s. There is no request body. The admin, the impersonated user, the organisation and the session\'s start are all read from the caller\'s verified token, so the endpoint cannot write an entry about anyone else.  Calling it again for the same session records nothing further.  Ending is the client signing out. For a Firebase session this endpoint revokes nothing: the refresh tokens belong to the impersonated user\'s Firebase account, so revoking them would also sign the real user out of their own sessions. A backend-issued session (an admin signed in through Keycloak) is ended here too, so its token stops authenticating. 
         * @summary End the impersonation this session is
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        endImpersonation(options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.endImpersonation(options).then((request) => request(axios, basePath));
        },
        /**
         * Generates a new set of 10 recovery codes for the user. Any existing unused recovery codes are deleted.  **Important:** Recovery codes are shown only once. The user must save them securely. 
         * @summary Generate new recovery codes
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        generateRecoveryCodes(options?: RawAxiosRequestConfig): AxiosPromise<RecoveryCodesResponse> {
            return localVarFp.generateRecoveryCodes(options).then((request) => request(axios, basePath));
        },
        /**
         * Who is acting as whom in the caller\'s impersonated session, and until when — what the app shows in its impersonation banner and counts down to.  A Firebase session also carries this in its token\'s claims. A session an admin signed in through Keycloak started is an opaque token issued by the backend, which the app cannot read, so this is where the app learns it (ADR-045). Everything is read from the caller\'s verified session. 
         * @summary The impersonation this session is
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getImpersonationSession(options?: RawAxiosRequestConfig): AxiosPromise<ImpersonationSessionResponse> {
            return localVarFp.getImpersonationSession(options).then((request) => request(axios, basePath));
        },
        /**
         * Returns the current MFA status for the authenticated user
         * @summary Get MFA status
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getMfaStatus(options?: RawAxiosRequestConfig): AxiosPromise<MfaStatusResponse> {
            return localVarFp.getMfaStatus(options).then((request) => request(axios, basePath));
        },
        /**
         * Records that the user has disabled MFA. Call this after removing all MFA factors in Firebase. This also deletes all recovery codes. 
         * @summary Record MFA disabled
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        recordMfaDisabled(options?: RawAxiosRequestConfig): AxiosPromise<MfaStatusResponse> {
            return localVarFp.recordMfaDisabled(options).then((request) => request(axios, basePath));
        },
        /**
         * Records that the user has enrolled in MFA. Call this after successfully completing MFA enrollment in Firebase. 
         * @summary Record MFA enrollment
         * @param {MfaEnrollmentRequest} mfaEnrollmentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        recordMfaEnrollment(mfaEnrollmentRequest: MfaEnrollmentRequest, options?: RawAxiosRequestConfig): AxiosPromise<MfaStatusResponse> {
            return localVarFp.recordMfaEnrollment(mfaEnrollmentRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Synchronizes the user\'s MFA status from Firebase to the database. Call this after enrolling or unenrolling MFA factors in Firebase. 
         * @summary Sync MFA status from Firebase
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        syncMfaStatus(options?: RawAxiosRequestConfig): AxiosPromise<MfaStatusResponse> {
            return localVarFp.syncMfaStatus(options).then((request) => request(axios, basePath));
        },
        /**
         * Verifies and consumes a recovery code. Used for account recovery when the user cannot use their normal MFA method. 
         * @summary Verify a recovery code
         * @param {VerifyRecoveryCodeRequest} verifyRecoveryCodeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        verifyRecoveryCode(verifyRecoveryCodeRequest: VerifyRecoveryCodeRequest, options?: RawAxiosRequestConfig): AxiosPromise<VerifyRecoveryCode200Response> {
            return localVarFp.verifyRecoveryCode(verifyRecoveryCodeRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * SecurityApi - object-oriented interface
 */
export class SecurityApi extends BaseAPI {
    /**
     * Records that an admin ended the impersonated session the caller is in, as an `IMPERSONATION_ENDED` entry in the admin audit log beside the session\'s `IMPERSONATION_STARTED` (#4680).  It is called by the **impersonated** session and not from the admin portal: the tab ending an impersonation holds the impersonated user\'s token, not the admin\'s. There is no request body. The admin, the impersonated user, the organisation and the session\'s start are all read from the caller\'s verified token, so the endpoint cannot write an entry about anyone else.  Calling it again for the same session records nothing further.  Ending is the client signing out. For a Firebase session this endpoint revokes nothing: the refresh tokens belong to the impersonated user\'s Firebase account, so revoking them would also sign the real user out of their own sessions. A backend-issued session (an admin signed in through Keycloak) is ended here too, so its token stops authenticating. 
     * @summary End the impersonation this session is
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public endImpersonation(options?: RawAxiosRequestConfig) {
        return SecurityApiFp(this.configuration).endImpersonation(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Generates a new set of 10 recovery codes for the user. Any existing unused recovery codes are deleted.  **Important:** Recovery codes are shown only once. The user must save them securely. 
     * @summary Generate new recovery codes
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public generateRecoveryCodes(options?: RawAxiosRequestConfig) {
        return SecurityApiFp(this.configuration).generateRecoveryCodes(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Who is acting as whom in the caller\'s impersonated session, and until when — what the app shows in its impersonation banner and counts down to.  A Firebase session also carries this in its token\'s claims. A session an admin signed in through Keycloak started is an opaque token issued by the backend, which the app cannot read, so this is where the app learns it (ADR-045). Everything is read from the caller\'s verified session. 
     * @summary The impersonation this session is
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getImpersonationSession(options?: RawAxiosRequestConfig) {
        return SecurityApiFp(this.configuration).getImpersonationSession(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Returns the current MFA status for the authenticated user
     * @summary Get MFA status
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getMfaStatus(options?: RawAxiosRequestConfig) {
        return SecurityApiFp(this.configuration).getMfaStatus(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Records that the user has disabled MFA. Call this after removing all MFA factors in Firebase. This also deletes all recovery codes. 
     * @summary Record MFA disabled
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public recordMfaDisabled(options?: RawAxiosRequestConfig) {
        return SecurityApiFp(this.configuration).recordMfaDisabled(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Records that the user has enrolled in MFA. Call this after successfully completing MFA enrollment in Firebase. 
     * @summary Record MFA enrollment
     * @param {MfaEnrollmentRequest} mfaEnrollmentRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public recordMfaEnrollment(mfaEnrollmentRequest: MfaEnrollmentRequest, options?: RawAxiosRequestConfig) {
        return SecurityApiFp(this.configuration).recordMfaEnrollment(mfaEnrollmentRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Synchronizes the user\'s MFA status from Firebase to the database. Call this after enrolling or unenrolling MFA factors in Firebase. 
     * @summary Sync MFA status from Firebase
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public syncMfaStatus(options?: RawAxiosRequestConfig) {
        return SecurityApiFp(this.configuration).syncMfaStatus(options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Verifies and consumes a recovery code. Used for account recovery when the user cannot use their normal MFA method. 
     * @summary Verify a recovery code
     * @param {VerifyRecoveryCodeRequest} verifyRecoveryCodeRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public verifyRecoveryCode(verifyRecoveryCodeRequest: VerifyRecoveryCodeRequest, options?: RawAxiosRequestConfig) {
        return SecurityApiFp(this.configuration).verifyRecoveryCode(verifyRecoveryCodeRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



